import { Button } from '@jouzen/ecom-components/v3/Button';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useGateValue } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';
import { type ReactNode } from 'react';

import AffirmPromo, {
  getAffirmPrice,
} from '@/app/[locale]/product/_components/AffirmPromo';
import usePDPExperiment from '@/app/[locale]/product/_components/HardwarePDP/_hooks/usePDPExperiment';
import useShowPDPAffirmMessage from '@/app/[locale]/product/_hooks/useShowPDPAffirmMessage';
import type { ListProduct } from '@/queries/Products';

import {
  PDP_NEW_WARRANTY_INTERSTITIAL,
  PDP_WARRANTY_INTERSTITIAL,
} from '../_consts/pdpVariants';
import type { BuildHardwareOrderBaseProps } from '../_types/order';
import { getPriceAndDiscount } from '../_utils/getPriceAndDiscount';
import AddOnModal from '../AddOnModal';

export interface OrderSummaryProps
  extends Pick<
    BuildHardwareOrderBaseProps,
    | 'order'
    | 'monthlyMembership'
    | 'twoYearWarranty'
    | 'threeYearWarranty'
    | 'yearlyMembership'
  > {
  readonly isAddingToCart: boolean;
  readonly isSelectingSize: boolean;
  readonly onAddToCart: () => Promise<void>;
  readonly onRadioChange: (value: string) => void;
  readonly product: ListProduct | undefined;
  readonly ringCount: number;
  readonly tag?: ReactNode;
}

const OrderSummary = ({
  isAddingToCart,
  isSelectingSize,
  onAddToCart,
  onRadioChange,
  order,
  product,
  tag,
  ...rest
}: OrderSummaryProps) => {
  const isExtendedWarranty = useGateValue('extended_warranty');
  const variant = usePDPExperiment();
  const t = useTranslations();
  const shouldNotShowModal =
    !isExtendedWarranty &&
    (variant === PDP_WARRANTY_INTERSTITIAL ||
      variant === PDP_NEW_WARRANTY_INTERSTITIAL);
  const { discountPrice, price } = getPriceAndDiscount(product);
  const discountedProductPrice = product?.discountedPrice;

  const showAffirm = useShowPDPAffirmMessage(
    discountedProductPrice,
    product?.handle,
  );
  const affirmPrice = getAffirmPrice(discountPrice, price!);

  if (!product) {
    return null;
  }

  return (
    <div className="flex flex-col items-center lg:items-stretch">
      <div className="w-full max-w-sm rounded-xl bg-sandstone-300 p-6 max-lg:mb-4">
        {tag}
        {shouldNotShowModal ? (
          <Button
            className="w-full text-lg font-bold"
            data-cy="add-to-cart-button"
            disabled={isAddingToCart || isSelectingSize}
            loading={isAddingToCart}
            loadingTextEnabled
            size="large"
            variant="primary"
            {...rest}
          >
            {t('pdp_add_to_cart')}
          </Button>
        ) : (
          <AddOnModal
            isAddingToCart={isAddingToCart}
            isSelectingSize={isSelectingSize}
            location="summary card"
            onAddToCart={onAddToCart}
            onRadioChange={onRadioChange}
            order={order}
            {...rest}
          />
        )}
        {showAffirm && (
          <Typography className="mt-4 text-xs" align="center" Element="div">
            <AffirmPromo
              className="text-sandstone-500"
              pageType="product"
              price={affirmPrice}
            />
          </Typography>
        )}
      </div>
    </div>
  );
};

export default OrderSummary;
